/**
 * Two-step sign-in in Teslyn Cloud (docs/specs/cloud/mfa.md): Supabase Auth MFA with an authenticator app (TOTP),
 * the organisation's setting (`tenant_security`), who has it on (`tenant_mfa_members`) and the reset for a lost
 * phone (Edge Function `mfa-reset`). What a session may change is decided by the database; this file only asks.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { isMfaLevel, mfaCodeError, type MfaLevel } from '@teslyn/domain';

export interface Assurance {
  readonly aal: 'aal1' | 'aal2';
  /** The person has a verified authenticator app (Supabase's next level is aal2). */
  readonly hasFactor: boolean;
}

export interface Requirement {
  /** Tenants that require this person to use two-step sign-in. */
  readonly requiredTenants: readonly string[];
}

export interface Factor {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
}

export interface Enrolment {
  readonly factorId: string;
  /** An SVG data URL of the QR code to scan. */
  readonly qrCode: string;
  /** The same key as text, for typing into the app by hand. */
  readonly secret: string;
}

const level = (v: unknown): 'aal1' | 'aal2' => (v === 'aal2' ? 'aal2' : 'aal1');

export async function loadAssurance(sb: SupabaseClient): Promise<Assurance> {
  const { data, error } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return { aal: 'aal1', hasFactor: false };
  return { aal: level(data.currentLevel), hasFactor: data.nextLevel === 'aal2' };
}

/** The tenants that require two-step sign-in of this person. Before the database has it, nothing is required. */
export async function loadRequirement(sb: SupabaseClient): Promise<Requirement> {
  const { data, error } = await sb.rpc('my_mfa_requirement');
  if (error || !data) return { requiredTenants: [] };
  const r = data as { required_tenants?: unknown };
  return {
    requiredTenants: Array.isArray(r.required_tenants)
      ? r.required_tenants.filter((t): t is string => typeof t === 'string')
      : [],
  };
}

export async function listFactors(sb: SupabaseClient): Promise<Factor[]> {
  const { data, error } = await sb.auth.mfa.listFactors();
  if (error) throw new Error(`Could not read your authenticator apps: ${error.message}`);
  return (data?.totp ?? [])
    .filter((f) => f.status === 'verified')
    .map((f) => ({ id: f.id, name: f.friendly_name ?? 'Authenticator app', createdAt: f.created_at }));
}

/** Start setting up an app: Supabase answers the QR code and key; nothing counts until a code is verified. */
export async function startEnrolment(sb: SupabaseClient, name: string): Promise<Enrolment> {
  const { data, error } = await sb.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `${name.trim() || 'Authenticator app'} ${new Date().toISOString().slice(0, 16)}`,
  });
  if (error || !data) throw new Error(`Could not start setting up the app: ${error?.message ?? 'no answer'}`);
  const totp = (data as { totp?: { qr_code?: string; secret?: string } }).totp;
  return { factorId: data.id, qrCode: totp?.qr_code ?? '', secret: totp?.secret ?? '' };
}

/** Check a code from the app; the session becomes two-step (aal2). Throws the words to show. */
export async function verifyCode(sb: SupabaseClient, factorId: string, code: string): Promise<void> {
  const { error } = await sb.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw new Error(mfaCodeError(error.message));
}

/** Remove one of your own apps (Supabase needs a two-step session for that). */
export async function removeFactor(sb: SupabaseClient, factorId: string): Promise<void> {
  const { error } = await sb.auth.mfa.unenroll({ factorId });
  if (error)
    throw new Error(
      /aal2|assurance/i.test(error.message)
        ? 'Enter the code from your authenticator app first, then remove it.'
        : `Could not remove the app: ${error.message}`,
    );
}

export async function loadTenantLevel(sb: SupabaseClient, tenantId: string): Promise<MfaLevel> {
  const { data, error } = await sb
    .from('tenant_security')
    .select('mfa_required')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) return 'off';
  const v = (data as { mfa_required?: unknown } | null)?.mfa_required;
  return isMfaLevel(v) ? v : 'off';
}

export async function saveTenantLevel(sb: SupabaseClient, tenantId: string, level: MfaLevel): Promise<void> {
  const { error } = await sb
    .from('tenant_security')
    .upsert({ tenant_id: tenantId, mfa_required: level }, { onConflict: 'tenant_id' });
  if (error)
    throw new Error(
      /row-level security/i.test(error.message)
        ? 'Enter the code from your authenticator app first (two-step sign-in). Set it up for yourself above.'
        : `Could not save the setting: ${error.message}`,
    );
}

/** People of the tenant and how many authenticator apps each has (tenant admins and Braynewave). */
export async function loadMemberFactors(sb: SupabaseClient, tenantId: string): Promise<Map<string, number>> {
  const { data, error } = await sb.rpc('tenant_mfa_members', { p_tenant: tenantId });
  if (error) throw new Error(`Could not read who uses two-step sign-in: ${error.message}`);
  return new Map(((data ?? []) as { user_id: string; factors: number }[]).map((r) => [r.user_id, r.factors]));
}

/** A lost phone: remove the person's apps so they sign in with one step and set up a new one. */
export async function resetTwoStep(sb: SupabaseClient, userId: string, note: string): Promise<number> {
  const { data, error } = await sb.functions.invoke('mfa-reset', { body: { user_id: userId, note } });
  if (error) {
    const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
    const body = ctx?.json ? ((await ctx.json().catch(() => null)) as { error?: string } | null) : null;
    throw new Error(body?.error ?? `Could not reset two-step sign-in: ${error.message}`);
  }
  return Number((data as { factors_removed?: number } | null)?.factors_removed ?? 0);
}
